/**
 * AP-30-D: Die Rolle ist kein Rang mehr, sondern **ein Name**.
 *
 * Sie steht noch an einigen Stellen — in der Benutzerliste, im
 * Einladungsformular — und bezeichnet dort die zugewiesene Systemrolle. Für die
 * Frage „darf er das?" ist sie ohne Bedeutung; die beantwortet `permissions`.
 *
 * Der Bestand war hier schon vor der Umstellung unvollständig: `TIME_BOOKING`
 * fehlte, und ein Stempler fiel damit in jeder Hierarchie-Prüfung durch.
 */
export type UserRole =
  | 'SUPER_ADMIN'
  | 'ADMIN'
  | 'MANAGER'
  | 'EMPLOYEE'
  | 'READONLY'
  | 'TIME_BOOKING';

export interface AuthUser {
  id: string;
  email: string;
  /** AP-30 Stufe 3: die zugewiesenen Rollen — eine Anzeige, keine Erlaubnis. */
  roles: { key: string; name: string }[];
  locale: string;
  tenantId: string;
  /** AP-38-C: extern oder beschäftigt — eine externe Person kann ihren Zugang zurückgeben. */
  kind?: 'EMPLOYEE' | 'EXTERNAL';
  isActive: boolean;
  lastLoginAt: string | null;
  /** AP-11-A: gebuchte Module des Tenants (z.B. ["TIME_TRACKING", "CRM"]) */
  activeModules: string[];
  /**
   * AP-30-D: Die **wirksamen** Rechte — bereits geschnitten gegen die Module,
   * in denen dieser Benutzer einen Platz hat. Die Oberfläche blendet danach
   * Navigation, Routen und Schalter aus; das ist Bequemlichkeit, keine
   * Sicherheitsgrenze.
   */
  permissions: string[];
  /** AP-11-A: ISO 4217 Währung des Tenants (z.B. "EUR") */
  currency: string;
  /**
   * AP-29-E: Gehört dieser Benutzer zum **Betreiber**-Mandanten? Trägt den
   * Zugang zur Konsole — bewusst nicht die Rolle `SUPER_ADMIN`, die hat auch
   * der Administrator eines Kunden.
   */
  isOperator?: boolean;
  /** AP-38-E: Stand der eigenen Zwei-Faktor-Anmeldung (Hinweis „bis … einrichten", Profil). */
  zweiFaktor?: ZweiFaktorStatus | null;
  profile: {
    firstName: string | null;
    lastName: string | null;
    phone: string | null;
    avatar: string | null;
    jobTitle: string | null;
  } | null;
}

export interface LoginCredentials {
  email: string;
  password: string;
  /** AP-38-B: die Firma dieses Tabs — arbeitet die Person für mehrere, gilt sie. */
  tenantId?: string;
}

/** AP-38-B: eine Firma, für die die angemeldete Person arbeiten kann. */
export interface Mitgliedschaft {
  tenantId: string;
  name: string;
  /** Die Firma, in der dieser Tab gerade arbeitet. */
  aktuell: boolean;
  /** AP-38-C: extern oder beschäftigt */
  kind?: 'EMPLOYEE' | 'EXTERNAL';
  /** Hat die Person dort einen Platz in der Zeiterfassung? (PWA) */
  zeiterfassung: boolean;
}

export interface LoginResponse {
  accessToken: string;
  user: Pick<AuthUser, 'id' | 'email' | 'roles' | 'locale' | 'tenantId'> & {
    firstName: string | null;
    lastName: string | null;
  };
  /** AP-38-E: nach der Einrichtung, einmalig im Klartext. */
  recoveryCodes?: string[];
}

// ── Zwei-Faktor-Anmeldung (AP-38-E) ──────────────────────────────────────────

export type ZweiFaktorVerfahren = 'TOTP' | 'EMAIL';

export interface ZweiFaktorStatus {
  eingerichtet: boolean;
  verfahren: ZweiFaktorVerfahren | null;
  eingerichtetAm: string | null;
  restlicheCodes: number;
  /** Bis wann einzurichten ist (JJJJ-MM-TT) — nur, solange nicht eingerichtet und eine Frist gilt. */
  pflichtAb: string | null;
  /** Die Frist des Bestands überhaupt (JJJJ-MM-TT) — für die Benutzerliste. */
  frist: string | null;
  /** Nur in der Entwicklung abschaltbar. */
  abgeschaltet: boolean;
}

export interface RegistriertesGeraet {
  id: string;
  label: string | null;
  createdAt: string;
  lastUsedAt: string;
  expiresAt: string;
}

/** Nach dem Passwort fehlt noch ein Schritt — statt eines Tokens ein Ticket. */
export type ZweiterSchritt =
  | { schritt: 'ZWEITER_FAKTOR'; ticket: string; verfahren: ZweiFaktorVerfahren; email: string | null }
  | { schritt: 'EINRICHTEN'; ticket: string };

export interface EinrichtungStart {
  verfahren: ZweiFaktorVerfahren;
  /** TOTP: das Geheimnis zum Abtippen und der Link für den QR-Code. */
  geheimnis: string | null;
  otpauthUrl: string | null;
  /** E-Mail: wohin der Code ging (maskiert). */
  email: string | null;
}

export const istZweiterSchritt = (r: LoginResponse | ZweiterSchritt): r is ZweiterSchritt => 'schritt' in r;
